import React from 'react';

import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import Helmet from 'wanderu-component-helmet';
import NotFound from 'wanderu-component-not-found';
import { BusLoader } from 'wanderu-components';
import {
    fetchNewRoutesAction,
    newRoutesSelectors,
} from 'wanderu-ducks-seo';
import { localize } from 'wanderu-i18n';

import seoConfig from '../../../config/seo.locale';
import LinkSection from '../../components/LinkSection/LinkSection';
import LinkTable from '../../components/LinkTable/LinkTable';
import { generateLocaleAwareGuideId } from '../../utils';

class NewRoutes extends React.Component {
    static async serverFetchData(opts) {
        const {
            dispatch,
            params,
        } = opts;

        const {
            locale,
        } = params;

        const newRoutesGuideId = generateLocaleAwareGuideId({ ...params, guideType: 'new-routes' });
        await dispatch(fetchNewRoutesAction.fetchNewRoutes(newRoutesGuideId, locale));
    }

    componentDidMount() {
        const {
            match,
            isLoaded,
            newRoutesGuideId,
            fetchNewRoutes,
        } = this.props;

        if (!isLoaded) {
            const {
                locale,
            } = match.params;
            fetchNewRoutes(newRoutesGuideId, locale);
        }
    }

    componentDidUpdate(prevProps) {
        const {
            match,
            newRoutesGuideId,
        } = this.props;
        const {
            newRoutesGuideId: previousNewRoutesGuideId,
        } = prevProps;

        if (newRoutesGuideId !== previousNewRoutesGuideId) {
            const {
                locale,
            } = match.params;
            this.props.fetchNewRoutes(newRoutesGuideId, locale);
        }
    }

    render() {
        const {
            t,
            notFound,
            error,
            isFetching,
            data,
        } = this.props;

        if (notFound || error) {
            return <NotFound status={error?.status} />;
        }

        if (isFetching || !data) {
            return (
                <BusLoader large />
            );
        }

        const localizedData = data.map((link) => ({
            ...link,
            linkAnchorText: t('baseApp:new-service-link-text', {
                ...link,
            }),
        }));

        return (
            <>
                <Helmet
                    description={t('baseApp:new-service-meta-description')}
                    supportedLocales={seoConfig.supportedLocales}
                    title={t('baseApp:new-service-meta-title')}
                />
                <LinkSection>
                    <LinkTable
                        data={localizedData}
                        title={t('baseApp:new-service-link-title')}
                        titleProps={{
                            component: 'h1',
                        }}
                    />
                </LinkSection>
            </>
        );
    }
}

NewRoutes.propTypes = {
    data: PropTypes.array,
    error: PropTypes.object,
    fetchNewRoutes: PropTypes.func,
    isFetching: PropTypes.bool,
    isLoaded: PropTypes.bool,
    match: PropTypes.object,
    newRoutesGuideId: PropTypes.string,
    notFound: PropTypes.bool,
    t: PropTypes.func,
};

function mapStateToProps(state, ownProps) {
    const {
        params,
    } = ownProps.match;
    const newRoutesGuideId = generateLocaleAwareGuideId({ ...params, guideType: 'new-routes' });
    const data = newRoutesSelectors.selectNewRouteLinks(state, newRoutesGuideId);
    return {
        newRoutesGuideId,
        notFound: fetchNewRoutesAction.selectNotFound(state, newRoutesGuideId),
        error: fetchNewRoutesAction.selectError(state, newRoutesGuideId),
        isFetching: fetchNewRoutesAction.selectWorking(state, newRoutesGuideId),
        isLoaded: fetchNewRoutesAction.selectSuccess(state, newRoutesGuideId),
        data,
    };
}

const mapDispatchToProps = {
    fetchNewRoutes: fetchNewRoutesAction.fetchNewRoutes,
};

export {
    NewRoutes,
    mapStateToProps,
};

const localizedNewRoutes = localize(NewRoutes);
export default connect(mapStateToProps, mapDispatchToProps)(localizedNewRoutes);
